import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import { Formik } from 'formik';
import * as Yup from 'yup';

import { CircularProgress, Grid, TextField, Typography } from '@mui/material';

import SectionTitle from '@/components/atoms/fonts/SectionTitle';
import ButtonCallAction from '@/components/BI/buttonCallAction';
import useSnackbar from '@/hooks/useSnackbar';
import useSimulatorsService from '@/services-simuladores/simulators';
import useWAUService from '@/services/WAU';

import { SectionComponent } from './components/SectionComponent';
import { ContainerSection, Container, ContainerForm, ContainerButton } from './styles';
import { useSelector } from 'react-redux';
import { Label } from '@mui/icons-material';

const SimulatorsInputs = () => {
    const location = useLocation();
    const snackbar = useSnackbar();
    const { entityId, propId } = useParams();
    const { getSimulatorInputs, getCalculateSimulator } = useSimulatorsService();
    const { storeRedisData } = useWAUService();
    const navigate = useNavigate();
    const [simulator, setSimulator] = useState({ name: '' });
    const [loading, setLoading] = useState(false);
    const [loadingButton, setLoadingButton] = useState(false);
    const [sections, setSections] = useState([]);
    const [initialValues, setInitialValues] = useState({});
    const [validationSchema, setValidationSchema] = useState(Yup.object());
    const previousValues = location.state?.previousValues;
    const userId = useSelector((state) => state.auth.user.id);
    const enterpriseId = useSelector((state) => state.auth.user.enterprise.id);
    const segment = useSelector((state) => state.auth.currentSegment);
    const segmentName = segment === 2 ? 'Rehsult Grãos' : 'Rehsult Leite';

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            const simulatorData = await getSimulatorInputs(entityId);
            setSimulator(simulatorData.data);

            const loadedSections = simulatorData?.data?.inputs?.sections || [];
            const values = {};
            const validations = {};

            loadedSections.forEach((section) => {
                section.topics.forEach((topic) => {
                    topic.fields.forEach((field) => {
                        const key = `field_${field.id}`;

                        if (previousValues) {
                            const matched = previousValues.find((p) => p.cell === field.cell);
                            if (matched?.value !== undefined) {
                                if (field.unit_of_measurement === '%') {
                                    values[key] = (matched.value * 100).toString().replace('.', ',');
                                } else {
                                    values[key] = matched.value.toString().replace('.', ',');
                                }
                            } else {
                                values[key] = '';
                            }
                        } else {
                            values[key] = '';
                        }

                        if (field.format === 'number') {
                            validations[key] = Yup.string()
                                .required('Campo obrigatório')
                                .matches(/^\d+(,\d+)?$/, 'Somente números válidos com vírgula');
                        } else if (field.format === 'text') {
                            validations[key] = Yup.string().required('Campo obrigatório');
                        } else if (field.format === 'list') {
                            validations[key] = Yup.string().required('Selecione uma opção');
                        }
                    });
                });
            });

            setSections(loadedSections);
            setInitialValues(values);
            setValidationSchema(Yup.object().shape(validations));
            setLoading(false);
        };

        fetchData();
    }, [entityId]);

    const handleSubmit = async (values) => {
        setLoadingButton(true);
        const payload = [];

        try {
            sections.forEach((section) => {
                section.topics.forEach((topic) => {
                    topic.fields.forEach((field) => {
                        const fieldKey = `field_${field.id}`;
                        let fieldValue = values[fieldKey];

                        if (fieldValue !== undefined && fieldValue !== '') {
                            if (field.format === 'number' && typeof fieldValue === 'string') {
                                fieldValue = parseFloat(fieldValue.replace(',', '.'));
                                if (field.unit_of_measurement === '%') fieldValue = fieldValue / 100;
                            }

                            payload.push({
                                cell: field.cell,
                                value: fieldValue
                            });
                        }
                    });
                });
            });

            if (payload.length === 0) {
                snackbar('Preencha pelo menos 1 indicador', 'error');
                return;
            }

            const calculateSimulator = await getCalculateSimulator(entityId, {
                inputs: payload
            });

            await storeRedisData({
                segment: segment,
                enterpriseId: enterpriseId,
                userId: userId,
                event: 'simulator',
                property: propId
            });

            navigate(`/consultor/propriedades/${propId}/simulador_resultado`, {
                state: { calculateSimulator, inputs: payload }
            });
        } catch (error) {
            snackbar('Erro ao calcular simulador. Tente novamente.', 'error');
        } finally {
            setLoadingButton(false);
        }
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
                <CircularProgress />
            </div>
        );
    }

    return (
        <Container>
            <SectionTitle tituloString={simulator.name} />
            <ContainerForm>
                <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema} enableReinitialize>
                    {(formik) => (
                        <form onSubmit={formik.handleSubmit}>
                            <ContainerSection>
                                {sections.map((section) => (
                                    <SectionComponent
                                        key={section.id}
                                        section={section}
                                        values={formik.values}
                                        handleChange={formik.handleChange}
                                        errors={formik.errors}
                                        submitCount={formik.submitCount}
                                    />
                                ))}
                            </ContainerSection>

                            <ContainerButton>
                                <ButtonCallAction
                                    style={{ width: '174px' }}
                                    onClick={formik.submitForm}
                                    loading={loadingButton}
                                    disabled={loadingButton}
                                >
                                    Calcular
                                </ButtonCallAction>
                            </ContainerButton>
                        </form>
                    )}
                </Formik>
            </ContainerForm>
        </Container>
    );
};

export default SimulatorsInputs;
